import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiGlobe, FiLayers, FiSettings, FiZap } from 'react-icons/fi';
import SEO from '../common/SEO';
import HeaderTwo from '../common/header/HeaderTwo';
import ScrollTop from '../common/footer/ScrollTop';
import TimelineOne from '../elements/timeline/TimelineOne';
import SectionTitle from '../elements/sectionTitle/SectionTitle';
import ProcessTimelineSplit from '../elements/process/ProcessTimelineSplit';
import CasesCards from '../elements/cases/CasesCards';
import ContactOne from '../elements/contact/ContactOne';
import FooterTwo from '../common/footer/FooterTwo';
import Copyright from '../common/footer/Copyright';
import ScrollAnimation from '../common/ScrollAnimation';
import Card from '../elements/card/Card';
import { siteContent } from '../data/siteContent';
import { seo } from '../data/seo';
import { ReactTyped } from 'react-typed';
import { BackgroundGradientAnimation } from '../components/ui/background-gradient-animation';

const headerOffset = 80;

const Freelancer = () => {
  const location = useLocation();
  const heroTitleStatic =
    siteContent.hero.titleStatic ?? siteContent.hero.title ?? '';
  const heroTitleRotating = siteContent.hero.titleRotating ?? [];
  const heroAriaLabel =
    siteContent.hero.title ??
    [heroTitleStatic, heroTitleRotating.join(', ')].filter(Boolean).join(' ');

  useEffect(() => {
    const pathToAnchorId = {
      '/services': siteContent.services.anchorId,
      '/process': siteContent.process.anchorId,
      '/cases': siteContent.cases.anchorId,
      '/faq': siteContent.faq.anchorId,
      '/work-experience': siteContent.projects.anchorId,
      '/projects': siteContent.projects.anchorId,
      '/contact': siteContent.contact.anchorId,
    };

    const anchorId = location.hash
      ? location.hash.replace('#', '')
      : pathToAnchorId[location.pathname];

    if (!anchorId) return;

    // PageScrollTop scrolls to top; run after it.
    window.setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (!el) return;

      const elementPosition =
        el.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset - 70;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }, 0);
  }, [location.hash, location.pathname, location.key]);

  return (
    <>
      <SEO
        title={seo.home.title}
        description={seo.home.description}
        ogTitle={seo.home.ogTitle}
        ogDescription={seo.home.ogDescription}
        person={seo.person}
      />
      <main className="page-wrapper freelancer-page">
        <HeaderTwo btnStyle="btn-small" HeaderSTyle="header-transparent" />

        {/* Start Slider area  */}
        <BackgroundGradientAnimation
          containerClassName="slider-area slider-style-2 variation-3 hero-compact-desktop hero-overlay-strong bg-overlay theme-shape relative"
          className="relative z-10 h-full flex items-center"
        >
          <div className="container w-full">
            <div className="inner text-center">
              <h1
                className="title display-two mt--130 mb_sm--80 pt_sm--50"
                aria-label={heroAriaLabel}
              >
                {heroTitleStatic}&nbsp;
                <br />
                <span className="theme-gradient">
                  <ReactTyped
                    strings={heroTitleRotating}
                    typeSpeed={80}
                    backSpeed={5}
                    backDelay={1000}
                    loop
                  />
                </span>
              </h1>

              <p className="description text-white">
                {siteContent.hero.subtitle}
              </p>
              {!!siteContent.hero.bullets?.length && (
                <ul className="list-style--3 text-white mt--30 freelancer-hero-bullets">
                  {siteContent.hero.bullets.map((bullet, idx) => (
                    <li key={idx}>{bullet}</li>
                  ))}
                </ul>
              )}
              <div className="button-group mt--40 mt_sm--20">
                <a
                  className="btn-default btn-large round btn-icon"
                  href={siteContent.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                >
                  {siteContent.hero.ctaPrimary.label}
                </a>
                <Link
                  className="btn-default btn-large btn-border round btn-icon"
                  to={siteContent.hero.ctaSecondary.href}
                >
                  {siteContent.hero.ctaSecondary.label}
                </Link>
              </div>
              {!!siteContent.hero.microcopy && (
                <p className="description text-[#acacac] mt--20 mb--0 b1">
                  <small>{siteContent.hero.microcopy}</small>
                </p>
              )}
            </div>
          </div>
        </BackgroundGradientAnimation>
        {/* End Slider area  */}

        <div
          className="rn-service-area rn-section-gap pt--0"
          id={siteContent.services.anchorId}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <SectionTitle
                  textAlign="text-center"
                  radiusRounded=""
                  subtitle="What we build"
                  title={siteContent.services.title}
                  description={siteContent.services.subtitle}
                />
              </div>
            </div>

            <div className="row service-wrapper mt--40">
              {siteContent.services.items.map((item, idx) => {
                const Icon =
                  item.icon === 'rocket'
                    ? FiZap
                    : item.icon === 'layers'
                      ? FiLayers
                      : item.icon === 'settings'
                        ? FiSettings
                        : item.icon === 'sparkle'
                          ? FiGlobe
                          : null;

                return (
                  <div
                    className="col-lg-6 col-md-6 col-sm-6 col-12 mt--30 d-flex align-items-stretch"
                    key={idx}
                  >
                    <Card title={item.title} icon={Icon}>
                      <p className="description b1 color-gray mb--0">
                        {item.description}
                      </p>
                    </Card>
                  </div>
                );
              })}
            </div>

            <div className="row">
              <div className="col-lg-12 text-center mt--50">
                <a
                  className="btn-default btn-large round btn-icon"
                  href={siteContent.links.calendly}
                  target="_blank"
                  rel="noreferrer"
                >
                  Book a Call
                </a>
              </div>
            </div>
          </div>
        </div>
        {/*<AboutTwo />*/}

        <ProcessTimelineSplit
          id={siteContent.process.anchorId}
          eyebrow="Process"
          title={siteContent.process.title}
          description={siteContent.process.subtitle}
          steps={siteContent.process.steps}
          ctaLabel={siteContent.hero.ctaPrimary.label}
          ctaHref={siteContent.links.calendly}
        />

        <CasesCards
          id={siteContent.cases.anchorId}
          eyebrow="What I work on"
          title={siteContent.cases.title}
          subtitle={siteContent.cases.subtitle}
          items={siteContent.cases.items}
          ctaPrimaryLabel={siteContent.hero.ctaPrimary.label}
          ctaPrimaryHref={siteContent.links.calendly}
          ctaSecondaryLabel={siteContent.hero.ctaSecondary.label}
          ctaSecondaryHref={siteContent.hero.ctaSecondary.href}
        />

        <div
          className="rwt-timeline-area rn-section-gap pt--0"
          id={siteContent.projects.anchorId}
        >
          <div className="container">
            <SectionTitle
              textAlign="text-center"
              radiusRounded=""
              subtitle={'Work Experience'}
              title="Latest Projects"
              description="I’ve been building web products for over a decade, working across startups and product teams."
            />
            <TimelineOne />
          </div>
        </div>

        <div className="rwt-timeline-area rn-section-gap pt--0">
          <div className="container">
            <Card className="rn-callto-action style-5">
              <div className="row row--0 align-items-center content-wrapper">
                <div className="col-lg-12">
                  <div className="inner">
                    <div className="content text-center">
                      <ScrollAnimation
                        animateIn="fadeInUp"
                        animateOut="fadeInOut"
                        animateOnce={true}
                      >
                        <h2 className="title">{siteContent.cta.title}</h2>
                      </ScrollAnimation>
                      <ScrollAnimation
                        animateIn="fadeInUp"
                        animateOut="fadeInOut"
                        animateOnce={true}
                      >
                        <h6 className="subtitle">
                          {siteContent.cta.description}
                        </h6>
                      </ScrollAnimation>
                      <ScrollAnimation
                        animateIn="fadeInUp"
                        animateOut="fadeInOut"
                        animateOnce={true}
                      >
                        <div className="text-center">
                          <a
                            className="btn-default btn-icon"
                            href={siteContent.links.calendly}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {siteContent.cta.button.label}{' '}
                          </a>
                          {siteContent.cta.microcopy && (
                            <p className="mt--15 mb--0">
                              <small>{siteContent.cta.microcopy}</small>
                            </p>
                          )}
                        </div>
                      </ScrollAnimation>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div
          className="rwt-portfolio-area rn-section-gap pt--0"
          id={siteContent.faq.anchorId}
        >
          <div className="container">
            <SectionTitle
              textAlign="text-center"
              radiusRounded=""
              subtitle={'FAQ'}
              title={siteContent.faq.title}
              description=""
            />
            <div className="row row--30 mt--40">
              {siteContent.faq.items.map((item, idx) => (
                <div className="col-lg-6 col-12 mt--30" key={idx}>
                  <div className="working-list">
                    <h5 className="working-title">{item.q}</h5>
                    <p>{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          className="rwt-portfolio-area rn-section-gap pt--0"
          id={siteContent.contact.anchorId}
        >
          <div className="container">
            <SectionTitle
              textAlign="text-center"
              radiusRounded=""
              subtitle={'Schedule a Call'}
              title={siteContent.contact.title}
              description={siteContent.contact.description}
            />
            <ContactOne />
          </div>
        </div>

        <FooterTwo />
        <Copyright />
      </main>
      <ScrollTop />
    </>
  );
};
export default Freelancer;
